// insights.js — pure revenue-analytics derivations for the Jobs → Insights tab.
//
// Ported from the retired nyc-suite InsightsPanel (docs/RETIREMENT-PARITY.md §5),
// REVENUE-ONLY. The old panel also showed Net Profit and Margin % computed from
// productionCost + contractorCost — fields that have no input UI anywhere and are
// always 0, so every margin it displayed was fake (~100%). Those are deliberately
// NOT ported here or anywhere in the Insights feature.
//
// Contract: every export is a PURE function of (jobs, quotes, year) — plain data
// in, plain data out, no React, no I/O. The view renders; this lib computes. The
// tests (insights.test.js) depend on that separation — keep it.

import { getFirstCaptureDate } from "../../../lib/utils.js";
import { INDUSTRY_LABELS } from "../../../lib/constants.js";
import { jobRevenueParts, jobRevenue, jobTier, isRolledUpChangeOrderJob, partsList } from "./revenue.js";
import { valueProvided } from "../../../lib/builderHelpers.js";
import { isProBono } from "../../../lib/proBono.js";

// Revenue math lives in revenue.js (the shared module every money surface uses
// since the 2026-08-25 rollup fix); re-exported so existing importers keep working.
export { jobRevenue, jobTier };

export const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/* ═══ DATE HELPERS ═══ */
// Year off a leading ISO date string ("YYYY-MM-DD" or a full ISO timestamp).
export const getYear = (dateStr) => {
  if (!dateStr) return null;
  const y = parseInt(String(dateStr).slice(0, 4), 10);
  return Number.isNaN(y) ? null : y;
};
// Month (0-indexed) off the same leading-ISO shape.
export const getMonth = (dateStr) => {
  if (!dateStr) return null;
  const m = parseInt(String(dateStr).slice(5, 7), 10);
  return Number.isNaN(m) ? null : m - 1;
};

const daysBetween = (a, b) => {
  if (!a || !b) return null;
  const diff = Math.round((new Date(b) - new Date(a)) / 86400000);
  return Number.isNaN(diff) ? null : diff;
};

// A job's revenue date: first capture date, falling back to created date.
//
// NOTE (ambiguity/bug call): the old panel resolved YEAR from `first || createdAt`
// but MONTH from `getMonth(first) || getMonth(createdAt)`. Because getMonth is
// 0-indexed, a January capture (month 0 → falsy) silently fell back to the created
// month, and year/month could even come from different source dates. We resolve ONE
// date here and read both year and month off it — same stated intent (capture date,
// else created date), without the January mis-bucketing.
export const resolveJobDate = (job) => getFirstCaptureDate(job) || job?.createdAt || null;

// Live jobs only. `jobs` from useData is already the non-trashed list; this makes
// the pure functions self-contained (and testable) rather than trusting the caller.
const liveJobs = (jobs) => (jobs || []).filter((j) => j && !j.deletedAt);

/* ═══ REVENUE HELPERS ═══ */
// A quote doc that is NOT an original bid: change orders (linked to a job) and
// legacy final_invoice docs from the retired app. These carry real money but must
// never count as separate "quotes" in conversion stats — a job with two change
// orders is one win, not three.
const isSupplementalQuote = (q) =>
  !!(q?.changeOrderOfJobId || q?.changeOrderOfQuoteId) || q?.docType === "final_invoice";

/* ═══ YEAR SELECTOR ═══ */
// Distinct years present across jobs (by revenue date) and quotes (by quote date),
// newest first. Drives the year chips.
export function availableYears(jobs = [], quotes = []) {
  const set = new Set();
  liveJobs(jobs).forEach((j) => {
    const y = getYear(resolveJobDate(j));
    if (y) set.add(y);
  });
  (quotes || []).forEach((q) => {
    const y = getYear(q?.quoteDate);
    if (y) set.add(y);
  });
  return [...set].sort((a, b) => b - a);
}

/* ═══ A) REVENUE — YEAR-OVER-YEAR, TIERED AR ═══ */
// Every live job is AR (accepted quote), split by billing milestone into
// paid / outstanding / projected. totalCurrent is the full AR (all three tiers);
// the monthly bars carry the same total-AR number.
//
// Rollup (2026-08-25): change orders fold into their parent project via
// jobRevenueParts — the CO child job is skipped as a row, its money lands in
// the parent's month, and tiers bucket PER PART (a paid parent's unbilled CO
// stays projected, because COs bill independently).
//
// Pro bono (2026-09-11): a pro bono job is not AR. Its value given (valueProvided
// over its lines, gross of the Pro bono discount) is reported on its own as
// proBonoCurrent / proBonoPrevious, counted for EVERY row, change-order children
// included, before the rollup skip. AR then runs unchanged: revenue.js already
// values a pro bono row's own amount at $0, so a pro bono parent contributes
// nothing but its billable change orders still land, and a pro bono change
// order on a paid parent adds $0 to the parent (GO review 2026-09-11: an early
// return here used to lose both). A job with no lines is valued at its linked
// quote's lines. A pro bono parent with nothing billable adds no bar and no
// tier, because every part is $0.
export function revenueYoY(jobs = [], quotes = [], year) {
  const thisYear = year;
  const lastYear = year - 1;
  const monthly = Array.from({ length: 12 }, () => ({ current: 0, previous: 0 }));
  const tiers = { paid: 0, outstanding: 0, projected: 0 };
  let totalCurrent = 0;
  let totalPrevious = 0;
  let proBonoCurrent = 0;
  let proBonoPrevious = 0;

  const live = liveJobs(jobs);
  live.forEach((j) => {
    const dateStr = resolveJobDate(j);
    const y = getYear(dateStr);
    const m = getMonth(dateStr);
    if (m == null) return;
    if (isProBono(j)) {
      const value = proBonoJobValue(j, quotes);
      if (y === thisYear) proBonoCurrent += value;
      else if (y === lastYear) proBonoPrevious += value;
    }
    if (isRolledUpChangeOrderJob(j, live)) return;
    const parts = jobRevenueParts(j, quotes, live);
    if (y === thisYear) {
      monthly[m].current += parts.total;
      totalCurrent += parts.total;
      partsList(parts).forEach((p) => { tiers[p.tier] += p.amount; });
    } else if (y === lastYear) {
      monthly[m].previous += parts.total;
      totalPrevious += parts.total;
    }
  });

  const maxMonth = Math.max(...monthly.map((m) => Math.max(m.current, m.previous)), 1);
  return { monthly, totalCurrent, totalPrevious, ...tiers, proBonoCurrent, proBonoPrevious, maxMonth, thisYear, lastYear };
}

// The value a pro bono job gave away: its own lines first, else the lines of
// its linked quote (a job booked before lines were copied onto jobs).
export const proBonoJobValue = (job, quotes = []) => {
  const own = Array.isArray(job?.lineItems) && job.lineItems.length ? job.lineItems : null;
  const quoteLines = (quotes || []).find((q) => q && q.id === job?.quoteId && !q.deletedAt)?.lineItems;
  return valueProvided(own || quoteLines || []);
};

/* ═══ B) CLIENT CONCENTRATION ═══ */
// Total AR per client (all tiers, incl. change orders) — concentration risk is
// about dependence on a client, so projected work counts as exposure too.
export function clientConcentration(jobs = [], quotes = [], year) {
  const byClient = {};
  let total = 0;

  // NOTE (CD10, deliberately NOT fixed here): keys by the denormalized
  // clientName string, so name variants split one client into two buckets.
  // The fix needs client records to exist — Phase 1 of the contact-ingest
  // work — so it lands there, not in this rollup pass.
  const live = liveJobs(jobs);
  live.forEach((j) => {
    if (isRolledUpChangeOrderJob(j, live)) return;
    if (getYear(resolveJobDate(j)) !== year) return;
    const rev = jobRevenueParts(j, quotes, live).total;
    const name = j.clientName || "Unknown";
    byClient[name] = (byClient[name] || 0) + rev;
    total += rev;
  });

  const sorted = Object.entries(byClient)
    .map(([name, rev]) => ({ name, rev, pct: total > 0 ? Math.round((rev / total) * 100) : 0 }))
    .sort((a, b) => b.rev - a.rev);

  const topPct = sorted[0]?.pct || 0;
  // Thresholds ported exactly (strict >): 0–25% Healthy, 26–40% Watch, 41%+ High Risk.
  const risk = topPct > 40 ? "high" : topPct > 25 ? "medium" : "healthy";

  return { sorted, total, topPct, risk, count: sorted.length };
}

/* ═══ C) QUOTE-TO-BOOK CONVERSION ═══ */
const isBookedQuote = (q) => q.jobStatus === "booked" || q.jobStatus === "completed";

export function quoteConversion(jobs = [], quotes = [], year) {
  // Win rate = booked / all non-draft ORIGINAL quotes in the year. Drafts excluded;
  // supplemental docs (change orders, legacy final invoices) excluded — they are
  // money on an existing win, not a new bid, and counting them inflated win rate
  // and avg won value (a job with two change orders read as 3 quotes / 3 wins).
  const yearQuotes = (quotes || []).filter(
    (q) => getYear(q?.quoteDate) === year && !q?.draft && !isSupplementalQuote(q)
  );
  const booked = yearQuotes.filter(isBookedQuote);
  const lost = yearQuotes.filter((q) => q.jobStatus === "lost");
  const open = yearQuotes.filter((q) => q.jobStatus === "quoted");

  const winRate = yearQuotes.length > 0 ? Math.round((booked.length / yearQuotes.length) * 100) : 0;
  const avgWon = booked.length > 0 ? Math.round(booked.reduce((s, q) => s + (Number(q.total) || 0), 0) / booked.length) : 0;
  const avgLost = lost.length > 0 ? Math.round(lost.reduce((s, q) => s + (Number(q.total) || 0), 0) / lost.length) : 0;

  // Days-to-book = quote date → linked job creation date, for booked quotes only.
  const live = liveJobs(jobs);
  const bookTimes = booked
    .map((q) => {
      const job = live.find((j) => j.quoteId === q.id);
      if (!job) return null;
      return daysBetween(q.quoteDate, job.createdAt);
    })
    .filter((d) => d != null && d >= 0);
  const avgDaysToBook = bookTimes.length > 0 ? Math.round(bookTimes.reduce((s, d) => s + d, 0) / bookTimes.length) : null;

  const byIndustry = {};
  yearQuotes.forEach((q) => {
    const ind = q.industry || "other";
    if (!byIndustry[ind]) byIndustry[ind] = { total: 0, booked: 0 };
    byIndustry[ind].total++;
    if (isBookedQuote(q)) byIndustry[ind].booked++;
  });

  return {
    total: yearQuotes.length,
    booked: booked.length,
    lost: lost.length,
    open: open.length,
    winRate,
    avgWon,
    avgLost,
    avgDaysToBook,
    byIndustry,
  };
}

/* ═══ D) REVENUE BY INDUSTRY ═══ */
// Total AR per industry (all tiers, incl. change orders) — same basis as the
// Revenue and Concentration sections so the numbers reconcile across the page.
export function revenueByIndustry(jobs = [], quotes = [], year) {
  const byInd = {};
  let total = 0;

  const live = liveJobs(jobs);
  live.forEach((j) => {
    if (isRolledUpChangeOrderJob(j, live)) return;
    if (getYear(resolveJobDate(j)) !== year) return;
    const rev = jobRevenueParts(j, quotes, live).total;
    const ind = j.industry || "other";
    byInd[ind] = (byInd[ind] || 0) + rev;
    total += rev;
  });

  return Object.entries(byInd)
    .map(([ind, rev]) => ({ ind, label: INDUSTRY_LABELS[ind] || ind, rev, pct: total > 0 ? Math.round((rev / total) * 100) : 0 }))
    .sort((a, b) => b.rev - a.rev);
}
